import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type React from "react"

import { type Selection } from "@heroui/react"

import type { RunRowLike } from "@/types/RunRow"

const areStringArraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false
  }

  return left.every((value, index) => value === right[index])
}

const areStringSetsEqual = (
  left: Set<React.Key>,
  rightValues: Iterable<string>,
) => {
  const right = new Set(rightValues)

  if (left.size !== right.size) {
    return false
  }

  for (const value of left) {
    if (typeof value !== "string" || !right.has(value)) {
      return false
    }
  }

  return true
}

interface UseRunResultsSelectionParams {
  scopedRunRows: RunRowLike[]
  onSelectionChange?: (selectedIds: string[]) => void
}

interface RunResultsSelectionController {
  selectedKeys: Selection
  setSelectedKeys: React.Dispatch<React.SetStateAction<Selection>>
  isSelectMode: boolean
  setIsSelectMode: React.Dispatch<React.SetStateAction<boolean>>
  selectableSimulationOutputIds: string[]
  selectableSimulationOutputIdSet: Set<string>
  selectedSimulationOutputIds: string[]
  selectedCount: number
  handleClearSelection: () => void
}

/**
 * Owns multi-select state for the run results table: which rows are selectable
 * (derived from the scoped rows), the current selection, select-mode toggle, and
 * keeping the selection pruned to selectable ids while notifying the parent when
 * it changes.
 */
export function useRunResultsSelection({
  scopedRunRows,
  onSelectionChange,
}: UseRunResultsSelectionParams): RunResultsSelectionController {
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set([]))
  const [isSelectMode, setIsSelectMode] = useState(false)

  const selectableSimulationOutputIds = useMemo(
    () =>
      scopedRunRows
        .map((row) => row.simulation_output_id)
        .filter(
          (simulationOutputId): simulationOutputId is string =>
            typeof simulationOutputId === "string" &&
            simulationOutputId.length > 0,
        ),
    [scopedRunRows],
  )

  const selectableSimulationOutputIdSet = useMemo(
    () => new Set(selectableSimulationOutputIds),
    [selectableSimulationOutputIds],
  )

  const selectedSimulationOutputIds = useMemo(() => {
    if (selectedKeys === "all") {
      return selectableSimulationOutputIds
    }

    return Array.from(selectedKeys).filter(
      (key): key is string =>
        typeof key === "string" &&
        selectableSimulationOutputIdSet.has(key) &&
        !key.startsWith("run-"),
    )
  }, [
    selectableSimulationOutputIdSet,
    selectableSimulationOutputIds,
    selectedKeys,
  ])

  const previousSelectedSimulationOutputIdsRef = useRef<string[] | null>(null)

  useEffect(() => {
    if (selectedKeys === "all") {
      setSelectedKeys(new Set(selectableSimulationOutputIds))
      return
    }

    const nextSelectedIds = Array.from(selectedKeys).filter(
      (key): key is string =>
        typeof key === "string" &&
        selectableSimulationOutputIdSet.has(key) &&
        !key.startsWith("run-"),
    )

    if (!areStringSetsEqual(selectedKeys, nextSelectedIds)) {
      setSelectedKeys(new Set(nextSelectedIds))
    }
  }, [
    selectableSimulationOutputIdSet,
    selectableSimulationOutputIds,
    selectedKeys,
  ])

  useEffect(() => {
    if (!onSelectionChange) {
      return
    }

    if (
      previousSelectedSimulationOutputIdsRef.current &&
      areStringArraysEqual(
        previousSelectedSimulationOutputIdsRef.current,
        selectedSimulationOutputIds,
      )
    ) {
      return
    }

    previousSelectedSimulationOutputIdsRef.current = selectedSimulationOutputIds
    onSelectionChange(selectedSimulationOutputIds)
  }, [onSelectionChange, selectedSimulationOutputIds])

  const handleClearSelection = useCallback(() => {
    setSelectedKeys(new Set([]))
    setIsSelectMode(false)
  }, [])

  const selectedCount = useMemo(() => {
    return selectedSimulationOutputIds.length
  }, [selectedSimulationOutputIds])

  return {
    selectedKeys,
    setSelectedKeys,
    isSelectMode,
    setIsSelectMode,
    selectableSimulationOutputIds,
    selectableSimulationOutputIdSet,
    selectedSimulationOutputIds,
    selectedCount,
    handleClearSelection,
  }
}
